import {announce} from '@github-ui/aria-live'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import type {CreateSavedViewResponse, CreateSavedViewVariables} from '../components/views/types'
import {LABELS} from '../constants/labels'
import {parseFetchErrorMessage, SAVED_VIEW_QUERY_KEY, SAVED_VIEWS_QUERY_KEY} from '../queries/saved-views'

interface UseCreateSavedViewMutationOptions {
  owner: string
  repo: string
}

export function useCreateSavedViewMutation({owner, repo}: UseCreateSavedViewMutationOptions) {
  const queryClient = useQueryClient()

  return useMutation<CreateSavedViewResponse, Error, CreateSavedViewVariables>({
    mutationFn: async variables => {
      const response = await verifiedFetchJSON(`/${owner}/${repo}/issues/views.json`, {
        method: 'POST',
        body: variables,
      })
      if (!response.ok) {
        throw new Error(await parseFetchErrorMessage(response, LABELS.views.createError))
      }
      return (await response.json()) as CreateSavedViewResponse
    },
    // Primes the single-view cache so the post-create redirect renders immediately, then invalidates the list.
    onSuccess: ({savedView}) => {
      queryClient.setQueryData([SAVED_VIEW_QUERY_KEY, owner, repo, String(savedView.id)] as const, {savedView})
      void queryClient.invalidateQueries({queryKey: [SAVED_VIEWS_QUERY_KEY, owner, repo]})
    },
    onError: () => announce(LABELS.views.createError, {assertive: true}),
  })
}
